import { useEffect, useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { handoffToolUrl } from "../../lib/websiteHandoff";
import { withLinkScheme } from "../../lib/sourceLink";
import { SetupNotice } from "./OnboardingChrome";

export type IncomingCheckResult = {
  /** "unverified" is a blocked source the user chose to fix later. */
  status: "readable" | "needs-attention" | "unverified";
  kind: "link" | "pdf" | "content";
  message: string;
  /** False when the check couldn't confirm the source; setup continues anyway. */
  confirmed?: boolean;
  reason?: string;
  businessWebsite?: string;
  /** A homepage Instagram profile has a bio or captions to make the first post from. */
  instagramPosts?: boolean;
  /** Page title or file name, when the check could read one. */
  title?: string;
  favicon?: string;
  youtube?: { videoId: string; thumbnailUrl: string; channel?: string; durationSec?: number };
};
export function needsIncomingContentCheck(intent: string | null) {
  const params = new URLSearchParams(intent || "");
  return ["articleUrl", "sourceDocument", "pendingContent"].some(
    (key) => !!params.get(key),
  );
}

/** A short check before brand setup, with recovery before the user invests time. */
export function IncomingContentCheck({
  intent,
  check,
  onResolved,
  onReplace,
}: {
  intent: string;
  check: (
    input: Record<string, string>,
    signal: AbortSignal,
  ) => Promise<IncomingCheckResult>;
  onResolved: (result: IncomingCheckResult) => void;
  onReplace: (intent: string) => void;
}) {
  const params = new URLSearchParams(intent);
  const [result, setResult] = useState<IncomingCheckResult | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [mode, setMode] = useState<"link" | "text">("link");
  const [link, setLink] = useState(params.get("articleUrl") || "");
  const [text, setText] = useState("");
  const [fieldError, setFieldError] = useState("");
  const originalPdf =
    params.has("sourceDocument") || params.get("source") === "pdf-to-carousel";
  const retryStoredSource = mode === "link" && !link.trim() &&
    (params.has("sourceDocument") || params.has("pendingContent"));
  useEffect(() => {
    const controller = new AbortController();
    const values: Record<string, string> = {};
    const source = new URLSearchParams(intent);
    ["articleUrl", "sourceDocument", "pendingContent", "claimToken"].forEach(
      (key) => {
        const value = source.get(key);
        if (value) values[key] = value;
      },
    );
    if (source.get("source") === "homepage") values.source = "homepage";
    setResult(null);
    check(values, controller.signal)
      .then((value) => {
        if (controller.signal.aborted) return;
        if (
          !["readable", "unverified", "needs-attention"].includes(value?.status)
        )
          throw new Error("Invalid check result");
        if (value.status === "needs-attention") setResult(value);
        // Only a definite problem stops setup; anything unconfirmed continues.
        else onResolved(value.status === "readable" ? value : { ...value, status: "readable", confirmed: false });
      })
      .catch(() => {
        if (!controller.signal.aborted)
          onResolved({
            status: "readable",
            kind: originalPdf ? "pdf" : "link",
            message: "",
            confirmed: false,
          });
      });
    return () => controller.abort();
  }, [intent, attempt, check, onResolved, originalPdf]);
  function replace() {
    if (retryStoredSource) {
      setAttempt((n) => n + 1);
      return;
    }
    let value = mode === "text" ? text.trim() : link.trim();
    if (mode === "link") {
      try {
        const url = new URL(withLinkScheme(value));
        if (
          !["https:", "http:"].includes(url.protocol) ||
          url.username ||
          url.password
        )
          throw new Error();
        value = url.href;
      } catch {
        setFieldError(
          "Enter a full public link, such as https://example.com/article.",
        );
        return;
      }
    }
    if (!value) {
      setFieldError("Paste the text you want to turn into a post.");
      return;
    }
    // Retry the same link without discarding its handoff/source context.
    if (mode === "link" && !params.has("pendingContent") && !params.has("sourceDocument")) {
      try {
        if (new URL(params.get("articleUrl") || "").href === value) {
          setAttempt((n) => n + 1);
          return;
        }
      } catch { /* A replacement is still allowed when the original URL was invalid. */ }
    }
    const next = new URLSearchParams(intent);
    [
      "articleUrl",
      "sourceDocument",
      "pendingContent",
      "claimToken",
      "inputText",
      "source",
      "toolUrl",
    ].forEach((key) => next.delete(key));
    next.set(mode === "text" ? "inputText" : "articleUrl", value);
    if (mode === "link" && params.get("source") === "homepage") next.set("source", "homepage");
    // Rechecking an unchanged URL is a retry, not a no-op state update.
    if (next.toString() === intent) setAttempt((n) => n + 1);
    else onReplace(next.toString());
  }
  const noun = result?.kind === "pdf" || originalPdf ? "PDF" : result?.kind === "content" ? "content" : "link";
  const segment = (active: boolean) =>
    `h-9 rounded-md px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
      active ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
    }`;
  return (
    <section className="onboarding-rise max-w-xl" aria-label="Check your source">
      <h1 className="onboarding-display text-[2.1rem] sm:text-5xl">
        {!result
          ? originalPdf
            ? "Checking your PDF…"
            : "Checking your link…"
          : `We can’t use this ${noun} yet.`}
      </h1>
      {!result ? (
        <div className="mt-8 flex items-center gap-4 rounded-2xl border bg-card p-4 shadow-sm">
          <span aria-hidden="true" className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[oklch(var(--brand)/0.1)]">
            <Loader2 className="h-5 w-5 animate-spin text-brand-ink motion-reduce:animate-none" />
          </span>
          <p role="status" className="text-sm text-muted-foreground">
            Making sure we can read {originalPdf ? "your PDF" : "the link"}. This
            usually takes a few seconds…
          </p>
        </div>
      ) : (
        <>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Swap in something we can read, or set up your workspace now and
            sort this out later.
          </p>
          <SetupNotice className="mt-7">{result.message}</SetupNotice>
          <form
            className="mt-8 space-y-5"
            onSubmit={(event) => {
              event.preventDefault();
              replace();
            }}
          >
            <div className="inline-flex rounded-lg bg-muted p-1" role="group" aria-label="Source type">
              <button
                type="button"
                aria-pressed={mode === "link"}
                className={segment(mode === "link")}
                onClick={() => {
                  setMode("link");
                  setFieldError("");
                }}
              >
                Use a link
              </button>
              <button
                type="button"
                aria-pressed={mode === "text"}
                className={segment(mode === "text")}
                onClick={() => {
                  setMode("text");
                  setFieldError("");
                }}
              >
                Paste text instead
              </button>
            </div>
            {mode === "link" ? (
              <div className="space-y-2">
                <Label htmlFor="incoming-link">Link for your post</Label>
                <Input
                  id="incoming-link"
                  className="h-11"
                  value={link}
                  onChange={(event) => {
                    setLink(event.target.value);
                    setFieldError("");
                  }}
                  aria-invalid={!!fieldError}
                  aria-describedby={fieldError ? "source-field-error" : undefined}
                  placeholder="https://example.com/article"
                  inputMode="url"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="incoming-text">Text for your post</Label>
                <Textarea
                  id="incoming-text"
                  rows={6}
                  maxLength={8000}
                  value={text}
                  placeholder="Paste the article, notes or transcript you want to turn into a post."
                  onChange={(event) => {
                    setText(event.target.value);
                    setFieldError("");
                  }}
                  aria-invalid={!!fieldError}
                  aria-describedby={fieldError ? "source-field-error" : undefined}
                />
              </div>
            )}
            {fieldError && (
              <p
                id="source-field-error"
                role="alert"
                className="text-sm text-destructive"
              >
                {fieldError}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button type="submit" variant="brand" className="h-11 rounded-md px-6 font-semibold">
                {mode === "text" ? "Use this text" : retryStoredSource ? originalPdf ? "Check this PDF" : "Check this source" : "Check this link"}
                <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="h-auto min-h-11 max-w-full whitespace-normal rounded-md px-5 py-2 text-muted-foreground hover:text-foreground"
                onClick={() => onResolved({ ...result, status: "unverified" })}
              >
                Set up workspace first, fix source later
              </Button>
            </div>
            {originalPdf && (
              <a
                className="inline-block text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
                href={handoffToolUrl(
                  new URLSearchParams({
                    ...Object.fromEntries(params),
                    source: "pdf-to-carousel",
                  }),
                )}
              >
                Return to upload another PDF
              </a>
            )}
          </form>
        </>
      )}
    </section>
  );
}
